/**
 * Identity Provider Create/Edit Modal
 *
 * Modal for registering and configuring identity providers for federated authentication.
 * Supports OIDC, SAML, OAuth2, FI-delegated, and API key protocols.
 *
 * Uses @aaimplatform/ui components and openapi-fetch typed client.
 */

import { useState, useEffect } from 'react';
import { Card, Button, Input, Select, Tabs, type TabItem } from '@aaimplatform/ui';
import { createIdentityProviderClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/identity-provider-registry';
import { authMiddleware } from '../api/client';
import { ClaimMappingEditor } from './ClaimMappingEditor';
import { RoleMappingEditor } from './RoleMappingEditor';
import { TestClaimMappingPanel } from './TestClaimMappingPanel';
import { formatApiError } from '../lib/api-error';

type IdentityProvider = components['schemas']['IdentityProvider'];
type IdentityProviderType = components['schemas']['IdentityProviderType'];
type CreateIdentityProviderRequest = components['schemas']['CreateIdentityProviderRequest'];
type RoleMappingRule = components['schemas']['RoleMappingRule'];

interface CreateIdpModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (provider: IdentityProvider) => void;
  editingProvider?: IdentityProvider | null;
  /**
   * Edit-mode delete affordance. When provided (edit mode only), the modal
   * footer shows a left-aligned "Delete Provider" action; the parent owns the
   * confirmation dialog + delete mutation. Passing this replaces the former
   * page-flow "Danger Zone" Alert on the Overview tab, which read as an error
   * banner sitting in the page (operator UX correction 2026-07-24).
   */
  onDelete?: () => void;
  /** True while the parent's delete mutation is in flight (disables the modal). */
  deleting?: boolean;
}

interface FormData {
  name: string;
  providerType: IdentityProviderType;
  providerFamily: string;
  // OAuth2/OIDC
  clientId?: string;
  clientSecret?: string;
  discoveryUrl?: string;
  redirectUri?: string;
  scopes?: string;
  // SAML
  samlMetadataUrl?: string;
  samlEntityId?: string;
  samlCertificate?: string;
  // MX_AUTH (Quiltt/MX federated FI-staff IdP, ADR-013 #1772)
  quilttInstitutionId?: string;
  // Claim mapping
  claimMappingClaims: Record<string, string>;
  roleMappingRules: RoleMappingRule[];
  // Common settings
  autoProvisionUsers: boolean;
  maxSessionDuration: number;
  requireMfa: boolean;
}

const MODAL_TABS: TabItem[] = [
  { id: 'configuration', label: 'Provider Configuration' },
  { id: 'mapping', label: 'Claim & Role Mapping' },
];

const PROVIDER_FAMILIES = [
  { value: 'google', label: 'Google Workspace' },
  { value: 'cuanswers', label: 'CU*Answers' },
  { value: 'jack_henry', label: 'Jack Henry' },
  { value: 'fiserv', label: 'Fiserv' },
  { value: 'finastra', label: 'Finastra' },
  { value: 'quiltt', label: 'Quiltt / MX' },
  { value: 'aaim_internal', label: 'Aaim Internal' },
  { value: 'custom', label: 'Custom Provider' },
];

const INITIAL_FORM_DATA: FormData = {
  name: '',
  providerType: 'oidc',
  providerFamily: 'google',
  autoProvisionUsers: true,
  maxSessionDuration: 3600,
  requireMfa: false,
  scopes: 'openid profile email',
  claimMappingClaims: {},
  roleMappingRules: [],
};

export function CreateIdpModal({ open, onClose, onSuccess, editingProvider, onDelete, deleting = false }: CreateIdpModalProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [activeTab, setActiveTab] = useState<string>('configuration');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = createIdentityProviderClient();
  // CSRF double-submit (N8) echo + auth-broken bounce on this mutating client.
  client.use(authMiddleware());

  useEffect(() => {
    if (editingProvider) {
      setFormData({
        name: editingProvider.name,
        providerType: editingProvider.providerType,
        providerFamily: editingProvider.providerFamily,
        clientId: editingProvider.clientId || '',
        clientSecret: '', // Never pre-fill secrets
        discoveryUrl: editingProvider.discoveryUrl || '',
        redirectUri: editingProvider.redirectUri || '',
        scopes: editingProvider.scopes?.join(' ') || '',
        samlMetadataUrl: editingProvider.samlMetadataUrl || '',
        samlEntityId: editingProvider.samlEntityId || '',
        samlCertificate: editingProvider.samlCertificate || '',
        quilttInstitutionId: editingProvider.quilttInstitutionId || '',
        claimMappingClaims: { ...(editingProvider.claimMapping?.claims ?? {}) },
        roleMappingRules: [...(editingProvider.claimMapping?.roleMapping ?? [])],
        autoProvisionUsers: editingProvider.autoProvisionUsers ?? true,
        maxSessionDuration: editingProvider.maxSessionDuration || 3600,
        requireMfa: editingProvider.requireMfa ?? false,
      });
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
    setActiveTab('configuration');
  }, [editingProvider, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Provider name is required';
    }

    if (!formData.providerFamily.trim()) {
      newErrors.providerFamily = 'Provider family is required';
    }

    // OAuth2/OIDC validation
    if (['oidc', 'oauth2'].includes(formData.providerType)) {
      if (!formData.clientId?.trim()) {
        newErrors.clientId = 'Client ID is required for OAuth2/OIDC';
      }
      if (!editingProvider && !formData.clientSecret?.trim()) {
        newErrors.clientSecret = 'Client secret is required for new OAuth2/OIDC providers';
      }
      if (!formData.discoveryUrl?.trim()) {
        newErrors.discoveryUrl = 'Discovery URL is required for OAuth2/OIDC';
      }
    }

    // SAML validation
    if (formData.providerType === 'saml') {
      if (!formData.samlMetadataUrl?.trim() && (!formData.samlEntityId?.trim() || !formData.samlCertificate?.trim())) {
        newErrors.saml = 'Either metadata URL or both entity ID and certificate are required';
      }
    }

    // MX_AUTH validation (Quiltt/MX federated FI-staff IdP, ADR-013 #1772)
    if (formData.providerType === 'mx_auth') {
      if (!formData.quilttInstitutionId?.trim()) {
        newErrors.quilttInstitutionId = 'Quiltt institution ID is required for MX Auth providers';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const hasClaimMapping =
        Object.keys(formData.claimMappingClaims).length > 0 ||
        formData.roleMappingRules.length > 0;

      const requestBody: CreateIdentityProviderRequest = {
        name: formData.name.trim(),
        providerType: formData.providerType,
        providerFamily: formData.providerFamily,
        ...(formData.clientId && { clientId: formData.clientId.trim() }),
        ...(formData.clientSecret && { clientSecret: formData.clientSecret }),
        ...(formData.discoveryUrl && { discoveryUrl: formData.discoveryUrl.trim() }),
        ...(formData.redirectUri && { redirectUri: formData.redirectUri.trim() }),
        ...(formData.scopes && { scopes: formData.scopes.split(/\s+/).filter(Boolean) }),
        ...(formData.samlMetadataUrl && { samlMetadataUrl: formData.samlMetadataUrl.trim() }),
        ...(formData.samlEntityId && { samlEntityId: formData.samlEntityId.trim() }),
        ...(formData.samlCertificate && { samlCertificate: formData.samlCertificate.trim() }),
        ...(formData.providerType === 'mx_auth' &&
          formData.quilttInstitutionId && {
            quilttInstitutionId: formData.quilttInstitutionId.trim(),
          }),
        ...(hasClaimMapping && {
          claimMapping: {
            claims: formData.claimMappingClaims,
            roleMapping: formData.roleMappingRules,
          },
        }),
        autoProvisionUsers: formData.autoProvisionUsers,
        maxSessionDuration: formData.maxSessionDuration,
        requireMfa: formData.requireMfa,
      };

      if (editingProvider) {
        const { data, error: apiError, response } = await client.PUT('/api/v1/identity-providers/{providerId}', {
          params: { path: { providerId: editingProvider.id } },
          body: requestBody,
        });

        if (apiError) {
          setError(formatApiError(apiError, 'Failed to update provider', response?.status));
          return;
        }

        if (data && 'success' in data && data.success && 'data' in data) {
          onSuccess(data.data);
          onClose();
        }
      } else {
        const { data, error: apiError, response } = await client.POST('/api/v1/identity-providers', {
          body: requestBody,
        });

        if (apiError) {
          setError(formatApiError(apiError, 'Failed to create provider', response?.status));
          return;
        }

        if (data && 'success' in data && data.success && 'data' in data) {
          onSuccess(data.data);
          onClose();
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const isOAuth = ['oidc', 'oauth2'].includes(formData.providerType);
  const isSAML = formData.providerType === 'saml';
  const isMxAuth = formData.providerType === 'mx_auth';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-sans font-semibold text-primary mb-6">
            {editingProvider ? 'Edit Identity Provider' : 'Register Identity Provider'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-sm">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <Tabs
            tabs={MODAL_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="mb-4"
          />

          <div className={activeTab === 'configuration' ? 'space-y-4' : 'hidden'}>
            {/* Provider Name */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Provider Name <span className="text-error">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Google Workspace - Aaim"
                className="w-full"
              />
              {errors.name && <span className="text-xs text-error mt-1 block">{errors.name}</span>}
            </div>

            {/* Provider Type */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Provider Type <span className="text-error">*</span>
              </label>
              <Select
                value={formData.providerType}
                onChange={(e) => setFormData({ ...formData, providerType: e.target.value as IdentityProviderType })}
                className="w-full"
                disabled={!!editingProvider}
              >
                <option value="oidc">OIDC (OpenID Connect)</option>
                <option value="saml">SAML 2.0</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="fi_delegated">FI-Delegated Auth</option>
                <option value="api_key">API Key</option>
                <option value="mx_auth">MX Auth (Quiltt/MX)</option>
              </Select>
            </div>

            {/* Provider Family */}
            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Provider Family <span className="text-error">*</span>
              </label>
              <Select
                value={formData.providerFamily}
                onChange={(e) => setFormData({ ...formData, providerFamily: e.target.value })}
                className="w-full"
              >
                {PROVIDER_FAMILIES.map((family) => (
                  <option key={family.value} value={family.value}>
                    {family.label}
                  </option>
                ))}
              </Select>
              {errors.providerFamily && <span className="text-xs text-error mt-1 block">{errors.providerFamily}</span>}
            </div>

            {/* OAuth2/OIDC Configuration */}
            {isOAuth && (
              <>
                <div className="border-t border-recessed pt-4 mt-4">
                  <h3 className="text-sm font-sans font-semibold text-primary mb-3">OAuth2/OIDC Configuration</h3>
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">
                    Client ID <span className="text-error">*</span>
                  </label>
                  <Input
                    value={formData.clientId || ''}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    placeholder="1234567890-abcdefg.apps.googleusercontent.com"
                    className="w-full"
                  />
                  {errors.clientId && <span className="text-xs text-error mt-1 block">{errors.clientId}</span>}
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">
                    Client Secret {!editingProvider && <span className="text-error">*</span>}
                  </label>
                  <Input
                    type="password"
                    value={formData.clientSecret || ''}
                    onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                    placeholder={editingProvider ? '(unchanged)' : 'Enter client secret'}
                    className="w-full"
                  />
                  {errors.clientSecret && <span className="text-xs text-error mt-1 block">{errors.clientSecret}</span>}
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">
                    Discovery URL <span className="text-error">*</span>
                  </label>
                  <Input
                    value={formData.discoveryUrl || ''}
                    onChange={(e) => setFormData({ ...formData, discoveryUrl: e.target.value })}
                    placeholder="https://accounts.google.com/.well-known/openid-configuration"
                    className="w-full"
                  />
                  {errors.discoveryUrl && <span className="text-xs text-error mt-1 block">{errors.discoveryUrl}</span>}
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">Redirect URI</label>
                  <Input
                    value={formData.redirectUri || ''}
                    onChange={(e) => setFormData({ ...formData, redirectUri: e.target.value })}
                    placeholder="https://api.aaim.com/auth/v1/callback"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">Scopes</label>
                  <Input
                    value={formData.scopes || ''}
                    onChange={(e) => setFormData({ ...formData, scopes: e.target.value })}
                    placeholder="openid profile email"
                    className="w-full"
                  />
                  <span className="text-xs text-tertiary mt-1 block">Space-separated list of OAuth scopes</span>
                </div>
              </>
            )}

            {/* SAML Configuration */}
            {isSAML && (
              <>
                <div className="border-t border-recessed pt-4 mt-4">
                  <h3 className="text-sm font-sans font-semibold text-primary mb-3">SAML Configuration</h3>
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">Metadata URL</label>
                  <Input
                    value={formData.samlMetadataUrl || ''}
                    onChange={(e) => setFormData({ ...formData, samlMetadataUrl: e.target.value })}
                    placeholder="https://idp.example.com/metadata.xml"
                    className="w-full"
                  />
                  <span className="text-xs text-tertiary mt-1 block">
                    Provide either metadata URL or entity ID + certificate
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">Entity ID</label>
                  <Input
                    value={formData.samlEntityId || ''}
                    onChange={(e) => setFormData({ ...formData, samlEntityId: e.target.value })}
                    placeholder="https://idp.example.com/entity"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">X.509 Certificate</label>
                  <textarea
                    value={formData.samlCertificate || ''}
                    onChange={(e) => setFormData({ ...formData, samlCertificate: e.target.value })}
                    rows={4}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    className="w-full px-3 py-2 border border-recessed rounded-sm font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                </div>

                {errors.saml && <span className="text-xs text-error mt-1 block">{errors.saml}</span>}
              </>
            )}

            {/* MX Auth (Quiltt/MX) Configuration */}
            {isMxAuth && (
              <>
                <div className="border-t border-recessed pt-4 mt-4">
                  <h3 className="text-sm font-sans font-semibold text-primary mb-3">MX Auth (Quiltt) Configuration</h3>
                </div>

                <div>
                  <label className="block text-sm font-sans font-medium text-primary mb-1">
                    Quiltt Institution ID <span className="text-error">*</span>
                  </label>
                  <Input
                    value={formData.quilttInstitutionId || ''}
                    onChange={(e) => setFormData({ ...formData, quilttInstitutionId: e.target.value })}
                    placeholder="inst_abc123"
                    className="w-full"
                  />
                  {errors.quilttInstitutionId && (
                    <span className="text-xs text-error mt-1 block">{errors.quilttInstitutionId}</span>
                  )}
                  <span className="text-xs text-tertiary mt-1 block">
                    Scopes the Quiltt Connect flow to this FI and backs the anti-spoof institution-id
                    check (ADR-013). Bind the FI tenant that uses this IdP under the Tenant Bindings tab
                    after the provider is created.
                  </span>
                </div>

                {/* Contextual help: the MX-Auth custom IdP method (ADR-013). This
                    SPA is Aaim-operator scope (AAIM_SUPER_ADMIN), so visibility is
                    already gated at the portal boundary. */}
                <div className="rounded-sm border border-recessed bg-surface px-4 py-3 space-y-2">
                  <p className="text-xs font-sans uppercase tracking-[0.15em] text-tertiary">
                    About MX Auth (custom IdP for FI staff)
                  </p>
                  <p className="text-xs text-secondary">
                    MX Auth authenticates an FI&apos;s staff by having them log into their own
                    institution&apos;s online banking through Quiltt/MX. Two proofs combine: the bank
                    login proves the FI-customer relationship, and the emailed invitation token
                    proves control of the invited address on first sign-in.
                  </p>
                  <p className="text-xs text-secondary">
                    The Quiltt institution ID is an opaque identifier (shape inst_...) that Quiltt
                    only reveals after a successful Connect against that institution -- it cannot be
                    looked up in advance. Capture it with the one-time fishing procedure
                    (docs/operations/mx-auth-institution-id-fishing-runbook.md): a friendly tester
                    at the FI runs an ordinary consumer Quiltt Connect and the operator reads the
                    resulting institution ID from consumer-api.
                  </p>
                  <p className="text-xs text-secondary">
                    The stored value is the FI&apos;s anti-spoof anchor, not a convenience: every staff
                    sign-in reads the connection&apos;s institution server-side from Quiltt and rejects
                    it unless it equals this value, and the identity service resolves this provider
                    BY the value. Keep exactly one active MX Auth provider per institution ID, and
                    exactly one active tenant binding -- multiples make staff sign-in fail closed.
                    Full onboarding recipe: docs/operations/mx-auth-fi-onboarding-runbook.md.
                  </p>
                </div>
              </>
            )}

            {/* Common Settings */}
            <div className="border-t border-recessed pt-4 mt-4">
              <h3 className="text-sm font-sans font-semibold text-primary mb-3">Security Settings</h3>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="autoProvisionUsers"
                checked={formData.autoProvisionUsers}
                onChange={(e) => setFormData({ ...formData, autoProvisionUsers: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="autoProvisionUsers" className="text-sm font-sans text-primary">
                Auto-provision users on first login
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="requireMfa"
                checked={formData.requireMfa}
                onChange={(e) => setFormData({ ...formData, requireMfa: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="requireMfa" className="text-sm font-sans text-primary">
                Require multi-factor authentication
              </label>
            </div>

            <div>
              <label className="block text-sm font-sans font-medium text-primary mb-1">
                Max Session Duration (seconds)
              </label>
              <Input
                type="number"
                value={formData.maxSessionDuration}
                onChange={(e) => setFormData({ ...formData, maxSessionDuration: parseInt(e.target.value) || 3600 })}
                min={300}
                max={86400}
                className="w-full"
              />
              <span className="text-xs text-tertiary mt-1 block">300 seconds (5 min) to 86400 seconds (24 hours)</span>
            </div>
          </div>

          <div
            className={
              activeTab === 'mapping'
                ? 'space-y-6 max-h-[60vh] overflow-y-auto pr-2'
                : 'hidden'
            }
          >
            <div>
              <h3 className="text-sm font-sans font-semibold text-primary mb-2">
                Claim Mapping
              </h3>
              <ClaimMappingEditor
                value={formData.claimMappingClaims}
                onChange={(next) =>
                  setFormData({ ...formData, claimMappingClaims: next })
                }
                disabled={loading}
              />
            </div>

            <div className="border-t border-recessed pt-6">
              <h3 className="text-sm font-sans font-semibold text-primary mb-2">
                Role Mapping
              </h3>
              <RoleMappingEditor
                value={formData.roleMappingRules}
                onChange={(next) =>
                  setFormData({ ...formData, roleMappingRules: next })
                }
                disabled={loading}
              />
            </div>

            <div className="border-t border-recessed pt-6">
              <TestClaimMappingPanel
                providerId={editingProvider?.id}
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 mt-6">
            {/* Left: destructive delete (edit mode only). Kept visually apart
                from the confirm/cancel pair so it is not a mis-click target,
                and rendered as a plain tertiary+error link rather than an
                Alert card so it does not read as an error in page flow
                (operator UX correction 2026-07-24). The parent owns the
                confirmation Dialog + delete mutation. */}
            {editingProvider && onDelete ? (
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={loading || deleting}
                className="text-error hover:bg-error/10"
              >
                {deleting ? 'Deleting...' : 'Delete Provider'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} disabled={loading || deleting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} disabled={loading || deleting}>
                {loading ? 'Saving...' : editingProvider ? 'Save Changes' : 'Create Provider'}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
